import React, { useState } from "react";
import { Modal, Button, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
    FaCalendarAlt, FaUsers, FaStore, FaFileInvoice,
    FaClipboardCheck, FaMapMarkerAlt, FaPhone, FaExternalLinkAlt
} from "react-icons/fa";

const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("ar-SY");
};

const formatTime = (value) => {
    if (!value) return "";
    const timePart = String(value).includes("T") ? String(value).split("T")[1] : String(value);
    return timePart.slice(0, 8);
};

const PatrolActivityModal = ({ show, onHide, data }) => {
    const [viewType, setViewType] = useState("establishments");
    const navigate = useNavigate();

    if (!data) return null;

    const {
        roster,
        members = [],
        totals = {},
        establishments = [],
        inspectionVisits = [],
        violationReports = [],
    } = data;

    const renderEmpty = (message) => (
        <p className="text-center text-muted py-4 mb-0">{message}</p>
    );

    const renderDetailSection = () => {
        switch (viewType) {
            case "establishments":
                return (
                    <div className="detail-list animate-fade">
                        <h6 className="border-bottom pb-2 mb-3 text-primary">
                            <FaStore className="ms-2" />
                            الفعاليات المضافة ({establishments.length})
                        </h6>
                        {establishments.length === 0 ? renderEmpty("لا توجد فعاليات مضافة") : establishments.map((est) => (
                            <div key={est.establishmentId} className="item-card shadow-sm p-3 mb-2 rounded border-end border-primary border-3">
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                    <strong className="text-dark">{est.name}</strong>
                                    <small className="text-muted">
                                        <FaMapMarkerAlt className="ms-1" />
                                        {[est.governorate, est.city, est.district].filter(Boolean).join(" — ")}
                                    </small>
                                </div>
                                <div className="text-muted x-small mt-2">
                                    <FaPhone className="ms-1" /> {est.phone || "—"}
                                    {est.addressText ? ` | ${est.addressText}` : ""}
                                </div>
                                <div className="d-flex flex-wrap gap-2 mt-2 x-small">
                                    <span className="badge bg-light text-primary border">زيارات: {est.inspectionVisitsCount || 0}</span>
                                    <span className="badge bg-light text-danger border">ضبوط: {est.violationReportsCount || 0}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                );
            case "visits":
                return (
                    <div className="detail-list animate-fade">
                        <h6 className="border-bottom pb-2 mb-3 text-info">
                            <FaClipboardCheck className="ms-2" />
                            الزيارات المنفذة ({inspectionVisits.length})
                        </h6>
                        {inspectionVisits.length === 0 ? renderEmpty("لا يوجد زيارات مسجلة") : inspectionVisits.map((visit) => (
                            <div key={visit.visitUid || visit.visitId} className="item-card shadow-sm p-3 mb-2 rounded border-end border-info border-3">
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                    <div>
                                        <strong className="text-dark d-block">{visit.establishmentName || "فعالية غير معروفة"}</strong>
                                        <span className="badge bg-info-subtle text-info border border-info mt-1">
                                            {visit.visitType || "—"}
                                        </span>
                                    </div>
                                    {visit.visitUid && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-info btn-sm rounded-pill px-3"
                                            onClick={() => {
                                                onHide();
                                                navigate(`/admin/visit-details/${visit.visitUid}`);
                                            }}
                                        >
                                            <FaExternalLinkAlt className="ms-1" />
                                            التفاصيل
                                        </button>
                                    )}
                                </div>
                                <div className="row g-2 mt-2 x-small text-muted">
                                    <div className="col-sm-6">
                                        <FaCalendarAlt className="ms-1 text-info" />
                                        {formatDate(visit.visitDate || visit.createdAtUtc)}
                                        {formatTime(visit.visitTime) ? ` — ${formatTime(visit.visitTime)}` : ""}
                                    </div>
                                    <div className="col-sm-6">
                                        <FaMapMarkerAlt className="ms-1" />
                                        {[visit.governorate, visit.city].filter(Boolean).join(" — ") || "—"}
                                    </div>
                                    {visit.missionNumber ? (
                                        <div className="col-12">
                                            رقم المهمة: <strong>{visit.missionNumber}</strong>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            case "violations":
                return (
                    <div className="detail-list animate-fade">
                        <h6 className="border-bottom pb-2 mb-3 text-danger">
                            <FaFileInvoice className="ms-2" />
                            الضبوط المنظمة ({violationReports.length})
                        </h6>
                        {violationReports.length === 0 ? renderEmpty("لا يوجد ضبوط منظمة") : violationReports.map((report) => (
                            <div key={report.reportUid || report.violationReportId} className="item-card shadow-sm p-3 mb-2 rounded border-end border-danger border-3">
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                    <div>
                                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                            <span className="badge bg-danger">ضبط #{report.reportNumber || "—"}</span>
                                            {report.missionNumber ? (
                                                <span className="badge bg-light text-dark border">مهمة {report.missionNumber}</span>
                                            ) : null}
                                        </div>
                                        <strong className="text-dark d-block">{report.establishmentName || "فعالية غير معروفة"}</strong>
                                        <div className="text-danger small mt-1">{report.violationTypeName || "—"}</div>
                                    </div>
                                    {report.reportNumber && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                            onClick={() => {
                                                onHide();
                                                navigate(`/admin/violation-details?id=${report.reportNumber}`);
                                            }}
                                        >
                                            <FaExternalLinkAlt className="ms-1" />
                                            التفاصيل
                                        </button>
                                    )}
                                </div>
                                <div className="row g-2 mt-2 x-small text-muted">
                                    <div className="col-sm-6">
                                        <FaCalendarAlt className="ms-1 text-danger" />
                                        {formatDate(report.reportDate || report.createdAtUtc)}
                                        {formatTime(report.reportTime) ? ` — ${formatTime(report.reportTime)}` : ""}
                                    </div>
                                    <div className="col-sm-6">
                                        <FaMapMarkerAlt className="ms-1" />
                                        {[report.governorate, report.city].filter(Boolean).join(" — ") || "—"}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered dir="rtl">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fs-5 fw-bold text-dark">
                    <FaCalendarAlt className="ms-2 text-primary" /> سجل نشاط الدورية: {roster.username}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {/* الجزء العلوي: معلومات عامة */}
                <div className="roster-info-header d-flex justify-content-between align-items-center mb-4 p-3 bg-primary bg-opacity-10 rounded-3">
                    <div>
                        <div className="fw-bold"><FaUsers className="ms-1" /> أعضاء الدورية:</div>
                        <div className="small">
                            {members.map(m => m.fullName).join(" - ")}
                        </div>
                    </div>
                    <div className="text-start">
                        <Badge bg="primary" className="p-2 px-3">
                            <FaCalendarAlt className="ms-1" /> {roster.patrolDate}
                        </Badge>
                    </div>
                </div>

                {/* مربعات الإحصائيات التفاعلية */}
                <div className="row g-3 mb-4">
                    <div className="col-md-4" onClick={() => setViewType('establishments')}>
                        <div className={`stat-box text-center p-3 rounded-4 cursor-pointer transition ${viewType === 'establishments' ? 'active-est shadow' : 'bg-light'}`}>
                            <FaStore className="fs-3 mb-2 text-primary" />
                            <h4 className="fw-bold mb-0">{totals.establishments}</h4>
                            <div className="text-muted small">فعاليات مضافة</div>
                        </div>
                    </div>
                    <div className="col-md-4" onClick={() => setViewType('visits')}>
                        <div className={`stat-box text-center p-3 rounded-4 cursor-pointer transition ${viewType === 'visits' ? 'active-visit shadow' : 'bg-light'}`}>
                            <FaClipboardCheck className="fs-3 mb-2 text-info" />
                            <h4 className="fw-bold mb-0">{totals.inspectionVisits}</h4>
                            <div className="text-muted small">زيارات تفتيشية</div>
                        </div>
                    </div>
                    <div className="col-md-4" onClick={() => setViewType('violations')}>
                        <div className={`stat-box text-center p-3 rounded-4 cursor-pointer transition ${viewType === 'violations' ? 'active-violation shadow' : 'bg-light'}`}>
                            <FaFileInvoice className="fs-3 mb-2 text-danger" />
                            <h4 className="fw-bold mb-0">{totals.violationReports}</h4>
                            <div className="text-muted small">ضبوط منظمة</div>
                        </div>
                    </div>
                </div>

                {/* قسم التفاصيل المتحرك */}
                <div className="details-container p-2 details-scroll">
                    {renderDetailSection()}
                </div>
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={onHide}>إغلاق</Button>
            </Modal.Footer>

            <style>{`
                .cursor-pointer { cursor: pointer; }
                .stat-box { border: 2px solid transparent; transition: all 0.3s ease; }
                .stat-box:hover { transform: translateY(-3px); }
                .active-est { background: #fff; border-color: #0d6efd; color: #0d6efd; }
                .active-visit { background: #fff; border-color: #0dcaf0; color: #0dcaf0; }
                .active-violation { background: #fff; border-color: #dc3545; color: #dc3545; }
                .item-card { background: #fff; transition: background 0.2s; }
                .item-card:hover { background: #f8f9fa; }
                .details-scroll { max-height: 360px; overflow-y: auto; }
                .bg-info-subtle { background-color: #cff4fc !important; }
                .x-small { font-size: 0.75rem; }
                .animate-fade { animation: fadeIn 0.4s ease-in; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </Modal>
    );
};

export default PatrolActivityModal;